import { __rest, __assign } from '../../node_modules/tslib/tslib.es6.js';
import css from '../../node_modules/@styled-system/css/dist/index.esm.js';
import { Box } from '../layout/Box.js';
import '../layout/Stack.js';
import { AnimatedBox } from '../layout/AnimatedBox.js';
import '../layout/Scrollbars.js';
import { AnimatePresence } from 'framer-motion';
import { useMergeRefs } from '../../hooks/useMergeRefs.js';
import React__default from 'react';
import { usePopper } from '../../node_modules/react-popper/lib/esm/usePopper.js';
import styled from 'styled-components';
import { PopoverContext } from './PopoverContext.js';
var PopoverContent = styled(AnimatedBox)(css({
  padding: "8px",
  borderRadius: "16px",
  backgroundColor: "popoverBackground",
  position: "relative",
  boxShadow: "0px 4px 12px rgba(0, 0, 0, 0.08)"
}));
var StyledPopoverContainer = styled(Box)({
  "&[data-popper-placement^='top'] > .popover-arrow": {
    bottom: "-6px"
  },
  "&[data-popper-placement^='bottom'] > .popover-arrow": {
    top: "-6px"
  },
  "&[data-popper-placement^='left'] > .popover-arrow": {
    right: "-6px"
  },
  "&[data-popper-placement^='right'] > .popover-arrow": {
    left: "-6px"
  }
});
var PopoverContainer = React__default.forwardRef(function (_a, ref) {
  var triggerRef = _a.triggerRef,
    onClose = _a.onClose,
    props = __rest(_a, ["triggerRef", "onClose"]);
  var containerRef = React__default.useRef(null);
  var mergedRef = useMergeRefs(containerRef, ref);
  React__default.useEffect(function () {
    function handleOutsideClick(event) {
      var _a;
      if (!((_a = containerRef.current) === null || _a === void 0 ? void 0 : _a.contains(event.target)) && !(triggerRef === null || triggerRef === void 0 ? void 0 : triggerRef.contains(event.target))) {
        onClose();
      }
    }
    document.addEventListener("mousedown", handleOutsideClick);
    return function () {
      document.removeEventListener("mousedown", handleOutsideClick);
    };
  }, [onClose, triggerRef]);
  return React__default.createElement(StyledPopoverContainer, __assign({}, props, {
    ref: mergedRef
  }));
});
var PopoverArrow = styled(AnimatedBox)(css({
  width: "16px",
  height: "16px",
  backgroundColor: "popoverBackground"
}));
var variants = {
  exit: {
    opacity: 0,
    scale: 0.95,
    transition: {
      duration: 0.1,
      ease: [0.4, 0, 1, 1]
    }
  },
  enter: {
    scale: 1,
    opacity: 1,
    transition: {
      duration: 0.15,
      ease: [0, 0, 0.2, 1]
    }
  }
};
var Popover = function Popover(_a) {
  var trigger = _a.trigger,
    children = _a.children,
    isFullWidth = _a.isFullWidth,
    placement = _a.placement,
    _b = _a.withArrow,
    withArrow = _b === void 0 ? true : _b;
  var _c = React__default.useState(false),
    isOpen = _c[0],
    setOpen = _c[1];
  var _d = React__default.useState(null),
    triggerElement = _d[0],
    setTriggerElement = _d[1];
  var _e = React__default.useState(null),
    popperElement = _e[0],
    setPopperElement = _e[1];
  var _f = React__default.useState(null),
    arrowElement = _f[0],
    setArrowElement = _f[1];
  var handleClose = React__default.useCallback(function () {
    return setOpen(false);
  }, []);
  var handleToggle = React__default.useCallback(function () {
    return setOpen(function (value) {
      return !value;
    });
  }, []);
  var contextValue = React__default.useMemo(function () {
    return {
      onClose: handleClose
    };
  }, [handleClose]);
  var _g = usePopper(triggerElement, popperElement, {
      modifiers: [{
        name: "arrow",
        options: {
          element: arrowElement
        }
      }, {
        name: "offset",
        options: {
          offset: [0, 14]
        }
      }, {
        name: "preventOverflow",
        options: {
          padding: 8
        }
      }],
      placement: placement
    }),
    styles = _g.styles,
    attributes = _g.attributes;
  return React__default.createElement(Box, null, React__default.createElement(PopoverContext.Provider, {
    value: contextValue
  }, React__default.createElement(Box, {
    ref: setTriggerElement,
    onClick: handleToggle,
    display: isFullWidth ? "block" : "inline-block"
  }, trigger), React__default.createElement(AnimatePresence, null, isOpen && React__default.createElement(PopoverContainer, __assign({
    ref: setPopperElement,
    triggerRef: triggerElement,
    style: styles.popper,
    onClose: handleClose
  }, attributes.popper, {
    key: "popover"
  }), React__default.createElement(PopoverContent, {
    variants: variants,
    initial: "exit",
    animate: "enter",
    exit: "exit"
  }, children), React__default.createElement("div", {
    ref: setArrowElement,
    style: styles.arrow,
    className: "popover-arrow"
  }, withArrow && React__default.createElement(PopoverArrow, {
    variants: variants,
    initial: "exit",
    animate: "enter",
    exit: "exit",
    style: {
      rotate: "45deg"
    }
  }))))));
};
export { Popover };